import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ShieldAlert, Droplets } from "lucide-react";
import { getService, SERVICES } from "../lib/services";
import { useSEO } from "../lib/seo";
import { BUSINESS } from "../lib/constants";
import { Reveal, Overline } from "../components/Reveal";
import { FAQAccordion } from "../components/FAQAccordion";
import { CTABand } from "../components/CTABand";

const ServicePage = () => {
  const { slug } = useParams();
  const service = getService(slug);
  useSEO(
    service ? `${service.title} | Kay's Carpet Cleaning` : "Kay's Carpet Cleaning",
    service ? service.meta : ""
  );
  if (!service) return <Navigate to="/" replace />;

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);
  const urgent = service.urgent;

  return (
    <div data-testid={`service-page-${slug}`}>
      <section className={`relative overflow-hidden ${urgent ? "bg-brand-dark" : ""}`}>
        {!urgent && <div aria-hidden="true" className="absolute -top-24 -right-32 w-[30rem] h-[30rem] rounded-full bg-brand-mist/70 blur-3xl" />}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-16 sm:pt-16 sm:pb-20 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <Overline>{urgent ? "Gold Coast · Time-Sensitive Help" : `Gold Coast · ${service.menu}`}</Overline>
            <h1 className={`mt-4 font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight ${urgent ? "text-white" : "text-brand-ink"}`} data-testid="service-page-h1">
              {urgent ? "Water or Flood Damage? Contact Kay's Carpet Cleaning" : service.title}
            </h1>
            <p className={`mt-6 text-base sm:text-lg leading-relaxed max-w-2xl ${urgent ? "text-white/85" : "text-brand-muted"}`}>
              {service.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              {urgent && (
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="service-urgent-call-button"
                  className="inline-flex items-center gap-2.5 bg-white text-brand-dark font-semibold px-7 py-4 rounded-full text-base transition-transform duration-300 hover:scale-[0.97]"
                >
                  <Phone size={18} />
                  {service.ctaCall}
                </a>
              )}
              <Link
                to="/get-estimate"
                data-testid="service-top-estimate-button"
                className={`group inline-flex items-center gap-2.5 font-semibold px-7 py-4 rounded-full text-base transition-[background-color,transform] duration-300 hover:scale-[0.97] ${
                  urgent
                    ? "border border-white/50 text-white hover:bg-white/10"
                    : "bg-brand hover:bg-brand-dark text-white shadow-[0_10px_30px_rgba(45,90,83,0.28)]"
                }`}
              >
                {service.ctaPrimary}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              {!urgent && (
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="service-top-call-button"
                  className="inline-flex items-center gap-2.5 bg-white border border-brand-line text-brand-ink font-semibold px-7 py-4 rounded-full text-base transition-[border-color,transform] duration-300 hover:border-brand hover:scale-[0.97]"
                >
                  <Phone size={18} className="text-brand" />
                  Call {BUSINESS.phoneDisplay}
                </a>
              )}
            </div>
            {urgent && (
              <p className="mt-6 text-sm text-white/70 flex items-center gap-2">
                <Droplets size={15} className="text-[#9FD3C7]" />
                Available Monday – Saturday, 8:00 AM – 8:00 PM
              </p>
            )}
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-tr-[3.5rem] rounded-bl-[3.5rem] rounded-tl-2xl rounded-br-2xl border border-brand-line/60 shadow-[0_30px_80px_rgba(45,90,83,0.18)]"
            >
              <img src={service.image} alt={service.imageAlt} loading="eager" className="w-full h-[300px] sm:h-[400px] object-cover" data-testid="service-page-image" />
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        {service.safety && (
          <Reveal className="mb-16">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-7 sm:p-9" data-testid="water-safety-section">
              <h2 className="font-display font-medium text-2xl sm:text-3xl tracking-tight text-brand-ink flex items-center gap-3">
                <ShieldAlert className="text-amber-600 shrink-0" size={26} />
                {service.safetyTitle}
              </h2>
              <ol className="mt-6 space-y-3.5">
                {service.safety.map((s, i) => (
                  <li key={i} className="flex items-start gap-3.5 text-sm sm:text-base text-brand-ink leading-relaxed">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        )}

        {service.sections.map((sec, i) => (
          <div key={sec.heading}>
            <Reveal className="mb-14">
              <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight text-brand-ink">{sec.heading}</h2>
              {sec.paragraphs?.map((p) => (
                <p key={p.slice(0, 32)} className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">{p}</p>
              ))}
              {sec.bullets && (
                <ul className="mt-5 space-y-2.5">
                  {sec.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-base text-brand-muted leading-relaxed">
                      <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
            {i === 1 && (
              <div className="mb-14">
                <CTABand id="mid" heading="Tell Us About Your Job" text="Send us the details — photos welcome — and Kay's Carpet Cleaning will provide an estimate based on your requirements." primaryLabel="Get My Estimate" />
              </div>
            )}
          </div>
        ))}

        {service.note && (
          <Reveal className="mb-14">
            <div className="bg-brand-mist/50 border border-brand-line/60 rounded-2xl px-7 py-6" data-testid="service-note">
              <p className="text-base sm:text-lg text-brand-ink font-medium leading-relaxed">{service.note}</p>
            </div>
          </Reveal>
        )}

        <Reveal className="mb-16">
          <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight text-brand-ink">{service.processTitle}</h2>
          <ol className="mt-8 space-y-0" data-testid="service-process">
            {service.process.map((p, i) => (
              <li key={p.title} className="relative flex gap-6 pb-8 last:pb-0">
                <div className="flex flex-col items-center">
                  <span className="w-10 h-10 rounded-full bg-brand text-white font-display font-semibold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                  {i < service.process.length - 1 && <span className="w-px flex-1 bg-brand-line mt-2" />}
                </div>
                <div className="pt-1.5 pb-2">
                  <h3 className="font-display font-medium text-lg text-brand-ink">{p.title}</h3>
                  <p className="mt-1.5 text-sm sm:text-base text-brand-muted leading-relaxed">{p.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </Reveal>

        {(service.prepBefore || service.prepAfter) && (
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {service.prepBefore && (
              <Reveal>
                <div className="bg-white border border-brand-line/60 rounded-2xl p-8 h-full shadow-[0_4px_40px_rgba(0,0,0,0.04)]" data-testid="prep-before-section">
                  <h2 className="font-display font-medium text-2xl sm:text-3xl tracking-tight text-brand-ink">Prior Treatment: Before We Arrive</h2>
                  <ul className="mt-6 space-y-4">
                    {service.prepBefore.map((p) => (
                      <li key={p.slice(0, 28)} className="flex items-start gap-3 text-sm sm:text-base text-brand-muted leading-relaxed">
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-brand shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
            {service.prepAfter && (
              <Reveal delay={0.08}>
                <div className="bg-brand-mist/40 border border-brand-line/60 rounded-2xl p-8 h-full" data-testid="prep-after-section">
                  <h2 className="font-display font-medium text-2xl sm:text-3xl tracking-tight text-brand-ink">Post Treatment: After We Leave</h2>
                  <ul className="mt-6 space-y-4">
                    {service.prepAfter.map((p) => (
                      <li key={p.slice(0, 28)} className="flex items-start gap-3 text-sm sm:text-base text-brand-muted leading-relaxed">
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-brand shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>
        )}

        <div className="mb-16">
          <CTABand id="pre-faq" heading={service.ctaHeading} text="The more detail you can share — including photos — the easier it is for us to understand your job." primaryLabel={service.ctaPrimary} />
        </div>

        <Reveal className="mb-16">
          <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight text-brand-ink mb-8">
            {service.menu} FAQs
          </h2>
          <FAQAccordion items={service.faqs} testIdPrefix="service-faq" />
        </Reveal>

        <CTABand id="final" heading="Ready to Talk About Your Cleaning?" text={service.ctaHeading} primaryLabel={service.ctaPrimary} />

        <Reveal className="mt-20">
          <h2 className="font-display font-medium text-2xl tracking-tight text-brand-ink mb-6">Other cleaning services</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`related-service-${r.slug}`}
                className="group flex items-center justify-between gap-4 bg-white border border-brand-line/60 rounded-xl px-5 py-4 hover:border-brand transition-colors duration-300"
              >
                <span className="text-sm font-semibold text-brand-ink group-hover:text-brand transition-colors duration-300">{r.menu}</span>
                <ArrowRight size={16} className="text-brand-muted group-hover:text-brand group-hover:translate-x-1 transition-[color,transform] duration-300" />
              </Link>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default ServicePage;
